import React from "react";
import { Check, ArrowRight, Lock, Flame } from "lucide-react";
import { usePrice } from "@/lib/price";

const free = [
  "Playbook Directory",
  "Job Directory · live ATS feed",
  "Resume Match (0-100 score)",
  "AI Toolbox · 178 entries",
  "Community read-only",
];
const all = [
  "Everything in Free",
  "Resume Builder · ATS-tuned",
  "Readiness Simulator · scored",
  "OmniCut · viral edit blueprints",
  "OmniScript (when it launches)",
  "Full community access (Whop)",
  "Every future tool we ship",
  "7-day refund · no questions",
];

export default function Plans() {
  const { price, remaining, cap } = usePrice();
  return (
    <section
      id="pricing"
      data-testid="plans"
      className="relative border-b-4 border-[#0a0a0a] bg-[#FDFBF7] overflow-hidden"
    >
      <div className="absolute -top-20 right-10 w-80 h-80 rounded-full bg-[#FFE600] border-4 border-[#0a0a0a] hidden md:block" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="text-center mb-12 reveal">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
            ONE PAYMENT · NOT A SUBSCRIPTION
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl">
            Start <span className="bg-[#FFE600] px-2">free.</span>
            <br />
            Unlock everything for{" "}
            <span className="bg-[#FF3B30] text-white px-2">{price}</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-stretch">
          {/* Free plan */}
          <div
            data-testid="plan-free"
            className="lg:col-span-4 bg-white border-4 border-[#0a0a0a] shadow-brutal p-7 md:p-8 reveal flex flex-col"
          >
            <div className="font-accent text-xs bg-[#0a0a0a] text-[#FFE600] inline-block px-2.5 py-1 self-start mb-3">
              FREE ACCOUNT
            </div>
            <div className="font-display text-7xl leading-none">$0</div>
            <p className="font-mono-body text-[13px] text-[#0a0a0a]/70 mt-3">
              No card. Look around. Use the free tools forever.
            </p>
            <a
              href="/login"
              data-testid="plan-free-cta"
              className="hover-pop mt-7 inline-flex items-center justify-center gap-2 bg-white text-[#0a0a0a] border-4 border-[#0a0a0a] px-5 py-3.5 font-accent text-base shadow-brutal-sm"
            >
              Create free account
              <ArrowRight size={16} strokeWidth={3} />
            </a>
            <ul className="space-y-3 mt-7">
              {free.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-2.5 font-mono-body text-[14px]"
                >
                  <Check size={16} className="text-[#0055FF] mt-0.5 shrink-0" strokeWidth={3} />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* All-Access */}
          <div
            data-testid="plan-all-access"
            className="lg:col-span-8 relative bg-[#FF3B30] text-white border-4 border-[#0a0a0a] shadow-brutal-lg p-7 md:p-10 reveal"
          >
            <span className="absolute -top-5 -left-3 bg-[#00FF66] text-[#0a0a0a] border-4 border-[#0a0a0a] px-4 py-2 font-accent text-xs shadow-brutal rotate-[-6deg]">
              <Flame size={12} strokeWidth={3} className="inline mr-1" />
              {remaining && cap ? (
                <>
                  {remaining.toLocaleString()} OF {cap.toLocaleString()} LAUNCH SPOTS LEFT
                </>
              ) : (
                <>FOUNDING PRICE · FIRST 2,500</>
              )}
            </span>
            <div className="font-accent text-xs bg-[#FFE600] text-[#0a0a0a] inline-block px-2.5 py-1 mb-3">
              ALL-ACCESS · LIFETIME
            </div>
            <div className="flex items-end gap-4 flex-wrap">
              <div className="font-display text-[96px] sm:text-[120px] md:text-[180px] leading-[0.8]">
                {price}
              </div>
              <div className="font-mono-body text-base md:text-lg pb-6">
                <span className="line-through opacity-60">$49</span>
                <br />
                one-time · lifetime
              </div>
            </div>
            <p className="font-mono-body text-[14px] mt-3 max-w-xl">
              One payment. Not a subscription. Working tools the minute you log in.
            </p>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mt-7 mb-7">
              {all.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-2.5 font-mono-body text-[14px]"
                >
                  <Check size={16} strokeWidth={3} className="mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://whop.com/checkout/plan_hfsvR7lsOjQds"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="plan-all-cta"
                className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#0a0a0a] px-7 py-4 font-accent text-lg md:text-xl shadow-brutal"
              >
                <Lock size={18} strokeWidth={3} /> LOCK {price} BEFORE IT RISES
                <ArrowRight size={18} strokeWidth={3} />
              </a>
              <a
                href="/login"
                data-testid="plan-all-secondary"
                className="hover-pop inline-flex items-center justify-center gap-2 bg-white text-[#0a0a0a] border-4 border-[#0a0a0a] px-6 py-4 font-accent text-base shadow-brutal"
              >
                Start free first
              </a>
            </div>

            <div className="mt-6 font-mono-body text-[11.5px] border-t-2 border-dashed border-white/40 pt-4">
              Powered by Whop · pay once · no recurring charge · 7-day refund
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
